import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Paper, Typography, Stack, Switch, TextField, Button, Box,
  Divider, IconButton, Alert, CircularProgress, FormControlLabel, Checkbox, Chip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // тиждень із понеділка

export default function Schedule() {
  const { t } = useTranslation();
  const { user, workspaceRole, activeWorkspaceId } = useAuth();
  const isManager = workspaceRole === 'owner' || workspaceRole === 'admin';
  const myId = user?.id;
  // Розклад: свій або (owner/admin) будь-якого працівника салону
  const [members, setMembers] = useState([]);
  const [targetMasterId, setTargetMasterId] = useState('');
  const WEEKDAY_NAMES = [
    t('scheduleP.sunday'), t('scheduleP.monday'), t('scheduleP.tuesday'),
    t('scheduleP.wednesday'), t('scheduleP.thursday'), t('scheduleP.friday'), t('scheduleP.saturday'),
  ];
  const [workHours, setWorkHours] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [warn, setWarn] = useState(''); // попап-попередження (конфлікт із бронню тощо)

  // форма додавання вихідного/блоку
  const [offDate, setOffDate] = useState('');
  const [fullDay, setFullDay] = useState(true);
  const [offStart, setOffStart] = useState('12:00');
  const [offEnd, setOffEnd] = useState('13:00');
  const [offReason, setOffReason] = useState('');
  const [applyToSalon, setApplyToSalon] = useState(false); // вихідний на весь салон

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.getSchedule(targetMasterId || undefined);
      setWorkHours(s.workHours || []);
      setTimeOff(s.timeOff || []);
    } catch (e) {
      setError(e.message || t('scheduleP.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [targetMasterId]);

  useEffect(() => { load(); }, [load]);

  // Список працівників салону для селектора (owner/admin)
  useEffect(() => {
    if (!isManager || !activeWorkspaceId) return;
    api.getWorkspaceMembers(activeWorkspaceId)
      .then((r) => setMembers(r.members || []))
      .catch(() => {});
  }, [isManager, activeWorkspaceId]);

  const rowFor = (wd) => workHours.find((r) => r.weekday === wd) || { weekday: wd, isWorking: false, startTime: '09:00', endTime: '18:00' };

  const updateRow = (wd, patch) => {
    setWorkHours((prev) => {
      const exists = prev.some((r) => r.weekday === wd);
      const base = exists ? prev : [...prev, rowFor(wd)];
      return base.map((r) => (r.weekday === wd ? { ...r, ...patch } : r));
    });
  };

  const saveHours = async () => {
    setSaving(true); setMsg(''); setError('');
    try {
      await api.updateWorkHours(workHours, targetMasterId || undefined);
      setMsg(t('scheduleP.saved'));
    } catch (e) {
      setError(e.message || t('scheduleP.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const addOff = async () => {
    setError(''); setMsg('');
    if (!offDate) { setError(t('scheduleP.pickDate')); return; }
    try {
      const payload = fullDay
        ? { date: offDate, reason: offReason }
        : { date: offDate, startTime: offStart, endTime: offEnd, reason: offReason };
      if (applyToSalon) payload.applyToSalon = true;
      else if (targetMasterId) payload.masterId = targetMasterId;
      const res = await api.addTimeOff(payload);
      setOffDate(''); setOffReason('');
      // Пакетний режим: показуємо підсумок (скільки додано / кого пропущено через записи)
      if (res?.bulk) {
        if (res.skipped?.length) {
          const list = res.skipped.map((s) => `• ${s.name} (${s.times})`).join('\n');
          setWarn(t('scheduleP.salonSummarySkipped', { created: res.created, total: res.total }) + '\n\n' + list);
        } else {
          setMsg(t('scheduleP.salonSummaryOk', { created: res.created }));
        }
      }
      await load();
    } catch (e) {
      // Конфлікт із активним записом (409) — показуємо помітним попапом; решта — інлайн
      if (e.status === 409) setWarn(e.message);
      else setError(e.message || t('scheduleP.addError'));
    }
  };

  const removeOff = async (id) => {
    try { await api.deleteTimeOff(id); await load(); }
    catch (e) { setError(e.message || t('scheduleP.deleteError')); }
  };

  if (loading) {
    return <Container sx={{ mt: 6, textAlign: 'center' }}><CircularProgress color="primary" /></Container>;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <Typography variant="h5">{t('scheduleP.title')}</Typography>
        {isManager && members.length > 0 && (
          <TextField
            select
            size="small"
            label={t('scheduleP.forMaster')}
            value={targetMasterId || (myId ?? '')}
            onChange={(e) => setTargetMasterId(String(e.target.value) === String(myId) ? '' : e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {members.map((m) => (
              <MenuItem key={m.userId} value={m.userId}>
                {m.name}{String(m.userId) === String(myId) ? ` (${t('scheduleP.you')})` : ''}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Dialog open={!!warn} onClose={() => setWarn('')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>⚠️ {t('common.attention')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary', whiteSpace: 'pre-line' }}>{warn}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarn('')} variant="contained" autoFocus>OK</Button>
        </DialogActions>
      </Dialog>


      {/* Тижневі години */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>{t('scheduleP.weeklyHours')}</Typography>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {DISPLAY_ORDER.map((wd) => {
            const r = rowFor(wd);
            return (
              <Stack key={wd} direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Box sx={{ width: 120 }}>
                  <FormControlLabel
                    control={<Switch checked={!!r.isWorking} onChange={(e) => updateRow(wd, { isWorking: e.target.checked })} />}
                    label={WEEKDAY_NAMES[wd]}
                  />
                </Box>
                {r.isWorking ? (
                  <>
                    <TextField type="time" size="small" label={t('scheduleP.from')} value={r.startTime}
                      onChange={(e) => updateRow(wd, { startTime: e.target.value })}
                      InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
                    <TextField type="time" size="small" label={t('scheduleP.to')} value={r.endTime}
                      onChange={(e) => updateRow(wd, { endTime: e.target.value })}
                      InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
                  </>
                ) : (
                  <Typography color="text.secondary" variant="body2">{t('scheduleP.dayOff')}</Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
        <Button variant="contained" sx={{ mt: 2 }} onClick={saveHours} disabled={saving}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </Paper>

      {/* Разові вихідні / закриті години */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>{t('scheduleP.extraDaysOff')}</Typography>

        <Stack spacing={2} sx={{ mt: 1, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <TextField type="date" size="small" label={t('scheduleP.date')} value={offDate}
              onChange={(e) => setOffDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <FormControlLabel
              control={<Checkbox checked={fullDay} onChange={(e) => setFullDay(e.target.checked)} />}
              label={t('scheduleP.fullDay')}
            />
            {!fullDay && (
              <>
                <TextField type="time" size="small" label={t('scheduleP.from')} value={offStart}
                  onChange={(e) => setOffStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
                <TextField type="time" size="small" label={t('scheduleP.to')} value={offEnd}
                  onChange={(e) => setOffEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
              </>
            )}
            <TextField size="small" label={t('scheduleP.reasonOptional')} value={offReason}
              onChange={(e) => setOffReason(e.target.value)} sx={{ minWidth: 180 }} />
            <Button variant="outlined" onClick={addOff}>{t('common.add')}</Button>
          </Stack>
          {isManager && (
            <FormControlLabel
              control={<Checkbox checked={applyToSalon} onChange={(e) => setApplyToSalon(e.target.checked)} />}
              label={t('scheduleP.applyToSalon')}
            />
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {timeOff.length === 0 ? (
          <Typography color="text.secondary">{t('scheduleP.noScheduledDaysOff')}</Typography>
        ) : (
          <Stack spacing={1}>
            {timeOff.map((off) => (
              <Stack key={off.id} direction="row" alignItems="center" justifyContent="space-between"
                sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  <Typography fontWeight={600}>{off.date}</Typography>
                  <Chip size="small" label={off.fullDay ? t('scheduleP.fullDay') : `${off.startTime}–${off.endTime}`} />
                  {off.reason && <Typography variant="body2" color="text.secondary">{off.reason}</Typography>}
                </Stack>
                <IconButton size="small" color="error" onClick={() => removeOff(off.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>
    </Container>
  );
}
